import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./EditImovelModal.css";
import { API_BASE_URL } from "../../../../config";
import Swal from 'sweetalert2';

const EditImovelModal = ({ isOpen, onClose, imovel, onSave }) => {
    const [formData, setFormData] = useState(null);
    const [isArrendado, setIsArrendado] = useState(false);
    const [codigoCCExists, setCodigoCCExists] = useState(false);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        if (imovel) {
            const plantadas = Number(imovel.num_arvores_plantadas) || 0;
            const cortadas = Number(imovel.num_arvores_cortadas) || 0;
            const remanescentes = plantadas - cortadas;
            const area_plantio = Number(imovel.area_plantio) || 0;
            const arvores_por_hectare = area_plantio > 0 ? remanescentes / area_plantio : 0;

            const formattedImovel = {
                ...imovel,
                data_plantio: formatDateForInput(imovel.data_plantio),
                data_contrato: formatDateForInput(imovel.data_contrato),
                vencimento_contrato: formatDateForInput(imovel.vencimento_contrato),
                data_divisao_percentual: formatDateForInput(imovel.data_divisao_percentual),
                num_arvores_remanescentes: remanescentes,
                num_arvores_por_hectare: arvores_por_hectare.toFixed(2)
            };
            setFormData(formattedImovel);
            setIsArrendado(!!imovel.arrendatario || !!imovel.data_contrato || !!imovel.percentual_arrendatario);
        }
    }, [imovel]);

    useEffect(() => {
        // Adicionando formData como dependência, mas usando uma verificação para evitar loop infinito se nada mudou realmente.
        // Uma alternativa melhor seria extrair esses cálculos para fora do useEffect ou usar useMemo se fossem apenas valores derivados para renderização,
        // mas como eles precisam atualizar o estado do formulário, vamos manter assim com cuidado.
        
        if (!formData || !isArrendado) return;
    
        const areaPlantioNum = parseFloat(formData.area_plantio) || 0;
        const percArrendatarioValue = formData.percentual_arrendatario;
    
        if (percArrendatarioValue && !isNaN(percArrendatarioValue)) {
            const percArrendatarioNum = parseFloat(percArrendatarioValue);
    
            if (percArrendatarioNum >= 0 && percArrendatarioNum <= 100) {
                const percArrendadores = 100 - percArrendatarioNum;
                const areaArrendatario = areaPlantioNum * (percArrendatarioNum / 100);
                const areaArrendadores = areaPlantioNum * (percArrendadores / 100);
    
                // Verificação crucial para evitar loop infinito
                if (
                    formData.percentual_arrendadores !== percArrendadores.toFixed(2) ||
                    formData.area_plantio_arrendatario !== areaArrendatario.toFixed(2) ||
                    formData.area_plantio_arrendadores !== areaArrendadores.toFixed(2)
                ) {
                    setFormData(prev => ({
                        ...prev,
                        percentual_arrendadores: percArrendadores.toFixed(2),
                        area_plantio_arrendatario: areaArrendatario.toFixed(2),
                        area_plantio_arrendadores: areaArrendadores.toFixed(2)
                    }));
                }
            }
        } else {
            // Limpa os campos calculados se não houver percentual válido
             if (
                formData.percentual_arrendadores !== "" ||
                formData.area_plantio_arrendatario !== "" ||
                formData.area_plantio_arrendadores !== ""
            ) {
                setFormData(prev => ({
                    ...prev,
                    percentual_arrendadores: "",
                    area_plantio_arrendatario: "",
                    area_plantio_arrendadores: ""
                }));
            }
        }
        // Adicionando formData às dependências para satisfazer o linter, 
        // mas confiando nas verificações internas para quebrar o loop.
    }, [formData, isArrendado]); 

    const handleChange = (e) => {
        const { id, value } = e.target;
        
        setFormData(prevData => {
            const newData = { ...prevData, [id]: value };

            if (['num_arvores_plantadas', 'num_arvores_cortadas', 'area_plantio'].includes(id)) {
                const plantadas = Number(newData.num_arvores_plantadas) || 0;
                const cortadas = Number(newData.num_arvores_cortadas) || 0;
                const remanescentes = plantadas - cortadas;
                newData.num_arvores_remanescentes = remanescentes;

                const area_plantio = Number(newData.area_plantio) || 0;
                const arvores_por_hectare = area_plantio > 0 ? remanescentes / area_plantio : 0;
                newData.num_arvores_por_hectare = arvores_por_hectare.toFixed(2);
            }
            return newData;
        });

        if (errors[id]) {
            setErrors(prevErrors => ({ ...prevErrors, [id]: null }));
        }

        if (id === "codigo_cc") {
            checkCodigoCCExists(value);
        }
    };

    const checkCodigoCCExists = async (codigo_cc) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        if (!codigo_cc || (imovel && codigo_cc === imovel.codigo_cc)) {
            setCodigoCCExists(false);
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/verificarCodigoCC?codigo_cc=${codigo_cc}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setCodigoCCExists(data.exists);
        } catch (error) {
            console.error("Erro de conexão ao verificar o código CC:", error);
        }
    };

    const toggleTipoImovel = (tipo) => {
        const isArrendadoNovo = tipo === "arrendado";
        setIsArrendado(isArrendadoNovo);
        setErrors({});
        if (!isArrendadoNovo) {
            setFormData((prev) => ({
                ...prev,
                arrendatario: "",
                data_contrato: "",
                vencimento_contrato: "",
                percentual_arrendatario: "",
                percentual_arrendadores: "",
                area_plantio_arrendatario: "",
                area_plantio_arrendadores: "",
                data_divisao_percentual: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (isArrendado) {
            if (!formData.arrendatario || !formData.arrendatario.trim()) {
                newErrors.arrendatario = "Arrendatário é obrigatório.";
            }
            if (!formData.data_contrato) {
                newErrors.data_contrato = "Data do contrato é obrigatória.";
            }
            if (!formData.vencimento_contrato) {
                newErrors.vencimento_contrato = "Vencimento do contrato é obrigatório.";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            Swal.fire({
                title: 'Atenção!',
                text: 'Por favor, preencha os campos obrigatórios.',
                icon: 'warning',
                confirmButtonText: 'OK',
                confirmButtonColor: '#28a745'
            });
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            Swal.fire({
                title: 'Erro de Autenticação',
                text: 'Sessão expirada. Faça login novamente.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#28a745'
            }).then(() => navigate('/'));
            return;
        }

        if (codigoCCExists) {
            Swal.fire({
                title: 'Erro!',
                text: 'O Código CC informado já está em uso.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#28a745'
            });
            return;
        }
        
        const toNullIfEmpty = value => (value === "" || value === null || value === undefined) ? null : value;

        const formattedData = {
            ...formData,
            arrendatario: isArrendado ? formData.arrendatario : null,
            data_contrato: isArrendado ? formData.data_contrato : null,
            vencimento_contrato: isArrendado ? formData.vencimento_contrato : null,
            percentual_arrendatario: isArrendado ? toNullIfEmpty(formData.percentual_arrendatario) : null,
            percentual_arrendadores: isArrendado ? toNullIfEmpty(formData.percentual_arrendadores) : null,
            area_plantio_arrendatario: isArrendado ? toNullIfEmpty(formData.area_plantio_arrendatario) : null,
            area_plantio_arrendadores: isArrendado ? toNullIfEmpty(formData.area_plantio_arrendadores) : null,
            data_divisao_percentual: isArrendado ? toNullIfEmpty(formData.data_divisao_percentual) : null,
        };

        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovel.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(formattedData),
            });
            
            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token');
                Swal.fire({
                    title: 'Erro de Autenticação',
                    text: 'Sua sessão expirou ou é inválida. Por favor, faça login novamente.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                }).then(() => navigate('/'));
                return;
            }

            if (response.ok) {
                onSave(); 
                Swal.fire({
                    title: 'Sucesso!',
                    text: 'Imóvel atualizado com sucesso!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                }).then((result) => {
                    if (result.isConfirmed) {
                        onClose();
                    }
                });
            } else {
                const errorData = await response.json();
                Swal.fire({
                    title: 'Erro!',
                    text: errorData.error || "Erro ao atualizar o imóvel.",
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            Swal.fire({
                title: 'Erro!',
                text: 'Erro de conexão ao tentar atualizar o imóvel.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#28a745'
            });
        }
    };

    if (!isOpen || !formData) return null;

    return (
        <div className="edit-modal-overlay">
            <div className="edit-modal-content">
                <div className="edit-modal-header">
                    <h2 className="edit-modal-title">Editar Imóvel</h2>
                </div>
                <div className="edit-modal-body">
                    <form id="edit-imovel-form" onSubmit={handleSubmit}>
                        <div className="segmented-control">
                            <button type="button" className={!isArrendado ? "active" : ""} onClick={() => toggleTipoImovel("proprio")}>Próprio</button>
                            <button type="button" className={isArrendado ? "active" : ""} onClick={() => toggleTipoImovel("arrendado")}>Arrendado</button>
                        </div>

                        <div className="form-grid">
                            {[
                                { id: "descricao", label: "Descrição", required: true, className: "full-width" },
                                { id: "codigo_cc", label: "Código CC", required: true, error: codigoCCExists && "Código CC já existe." },
                                { id: "proprietario", label: "Proprietário", required: true },
                                { id: "matricula", label: "Matrícula" },
                                { id: "numero_car", label: "Número CAR" },
                                { id: "numero_ccir", label: "Número CCIR" },
                                { id: "numero_itr", label: "Número ITR" },
                                { id: "municipio", label: "Município", required: true },
                                { id: "localidade", label: "Localidade", required: true },
                                { id: "area_imovel", label: "Área do Imóvel (ha)", type: "number", step: "0.01", required: true },
                                { id: "area_plantio", label: "Área de Plantio (ha)", type: "number", step: "0.01", required: true },
                                { id: "altura_desrama", label: "Altura da Desrama (m)", type: "number", step: "0.01" },
                                { id: "especie", label: "Espécie" },
                                { id: "origem", label: "Origem" },
                                { id: "num_arvores_plantadas", label: "Nº de Árvores Plantadas", type: "number" },
                                { id: "num_arvores_cortadas", label: "Nº de Árvores Cortadas", type: "number" },
                                { id: "num_arvores_remanescentes", label: "Nº de Árvores Remanescentes", type: "number", readOnly: true },
                                { id: "num_arvores_por_hectare", label: "Nº de Árvores por Hectare", type: "number", readOnly: true },
                                { id: "data_plantio", label: "Data de Plantio", type: "date" },
                            ].map(field => (
                                <div className={`input-group ${field.className || ''}`} key={field.id}>
                                    <label htmlFor={field.id} className="form-label">{field.label}</label>
                                    <input
                                        type={field.type || "text"}
                                        id={field.id}
                                        className="form-input"
                                        value={formData[field.id] || ""}
                                        onChange={handleChange}
                                        required={field.required}
                                        step={field.step}
                                        readOnly={field.readOnly} 
                                    />
                                    {field.error && <div className="error-message">{field.error}</div>}
                                </div>
                            ))}

                            {isArrendado && (
                                <>
                                    <div className="input-group">
                                        <label htmlFor="arrendatario" className="form-label">Arrendatário</label>
                                        <input type="text" id="arrendatario" className="form-input" value={formData.arrendatario || ""} onChange={handleChange} />
                                        {errors.arrendatario && <div className="error-message">{errors.arrendatario}</div>}
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="data_contrato" className="form-label">Data do Contrato</label>
                                        <input type="date" id="data_contrato" className="form-input" value={formData.data_contrato || ""} onChange={handleChange} />
                                        {errors.data_contrato && <div className="error-message">{errors.data_contrato}</div>}
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="vencimento_contrato" className="form-label">Vencimento do Contrato</label>
                                        <input type="date" id="vencimento_contrato" className="form-input" value={formData.vencimento_contrato || ""} onChange={handleChange} />
                                        {errors.vencimento_contrato && <div className="error-message">{errors.vencimento_contrato}</div>}
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="percentual_arrendatario" className="form-label">Percentual Arrendatário (%)</label>
                                        <input type="number" step="0.01" min="0" max="100" id="percentual_arrendatario" className="form-input" value={formData.percentual_arrendatario || ""} onChange={handleChange} />
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="percentual_arrendadores" className="form-label">Percentual Arrendadores (%)</label>
                                        <input type="number" id="percentual_arrendadores" className="form-input" value={formData.percentual_arrendadores || ""} readOnly />
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="area_plantio_arrendatario" className="form-label">Área Plantio Arrendatário (ha)</label>
                                        <input type="number" id="area_plantio_arrendatario" className="form-input" value={formData.area_plantio_arrendatario || ""} readOnly />
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="area_plantio_arrendadores" className="form-label">Área Plantio Arrendadores (ha)</label>
                                        <input type="number" id="area_plantio_arrendadores" className="form-input" value={formData.area_plantio_arrendadores || ""} readOnly />
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="data_divisao_percentual" className="form-label">Data da Divisão</label>
                                        <input type="date" id="data_divisao_percentual" className="form-input" value={formData.data_divisao_percentual || ""} onChange={handleChange} />
                                    </div>
                                </>
                            )}
                        </div>
                    </form>
                </div>
                <div className="edit-modal-footer">
                    <button type="button" className="modal-btn modal-btn-cancel" onClick={onClose}>Cancelar</button>
                    <button type="submit" form="edit-imovel-form" className="modal-btn modal-btn-success">
                        Salvar Alterações
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditImovelModal;